/**
 * @file Registration.js
 * @description Card for the Registration dashboard.
 */

import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { DASHBOARD_AS_OF_DATE_FORMAT } from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import LiquidGauge from 'react-liquid-gauge';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './Registration.scss';
import { getText } from '../../i18n';

// Returns the percentage of any given two numbers
const getPercentage = (numerator, denominator) => {
    if (!numerator || !denominator || denominator === 0) return 0;
    return (numerator / denominator) * 100;
};

// Helper method to return the outside circular linearGradient
// This is to fill the outside circle based on the given percent of the registered users
const getRegistrationPercentageGradient = percent => {
    const clampedPercent = Math.max(0, Math.min(percent, 100));
    const id = 'registrationGradient';

    // Return a linearGradient with no coloring if the given percent is 0
    if (clampedPercent.toFixed(0) === 0) {
        return (
            <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="0%">
                {/* Top grey */}
                <stop offset="0%" stopColor="#C4C4C4" />
                <stop offset="0%" stopColor="#C4C4C4" />
            </linearGradient>
        );
    }

    // linearGradient stop values calculated based off the percent
    const offset = 1 - clampedPercent / 100;
    const fadeRange = 0.15;
    const fadeStart = offset - fadeRange / 2;
    const fadeMid = offset;
    const fadeEnd = offset + fadeRange / 2;

    return (
        <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
            {/* Top grey */}
            <stop offset="0%" stopColor="#C4C4C4" />
            <stop offset={`${fadeStart * 100}%`} stopColor="#C4C4C4" />

            {/* Smooth transition zone */}
            <stop
                offset={`${fadeMid * 100}%`}
                stopColor="#8EECA2"
                stopOpacity="0.9"
            />
            <stop
                offset={`${fadeEnd * 100}%`}
                stopColor="#8EECA2"
                stopOpacity="1"
            />

            {/* Bottom green */}
            <stop offset="100%" stopColor="#8EECA2" />
        </linearGradient>
    );
};

const isRegistrationDataEmpty = registrationData => {
    return (
        !registrationData ||
        (registrationData.populationTotal === 0 &&
            registrationData.registeredUsers === 0 &&
            registrationData.activeUsers === 0 &&
            registrationData.newUsers === 0)
    );
};

const renderRegistrationTooltipContent = (
    registeredUsers,
    inactiveUsers,
    toolTipPosition,
) => {
    return (
        <div
            className="custom-registration-tooltip"
            style={{
                position: 'fixed',
                top: `${toolTipPosition.y}px`,
                left: `${toolTipPosition.x}px`,
            }}
        >
            <div className="registration-tooltip-header-label">
                {getText('msg_5cf89afb8bc8')}
            </div>
            <div className="registration-tooltip-content">
                <div className="registration-legend-dot-active" />
                <div className="registration-tooltip-count">
                    {registeredUsers.toLocaleString()}
                </div>
                <div className="registration-tooltip-legend-label">{getText('msg_a733b809d2f1')}</div>
            </div>
            <div className="registration-tooltip-content">
                <div className="registration-legend-dot-inactive" />
                <div className="registration-tooltip-count">
                    {inactiveUsers.toLocaleString()}
                </div>
                <div className="registration-tooltip-legend-label">
                    {getText('msg_09af574c7f20')}
                </div>
            </div>
        </div>
    );
};

const formatPercentageDisplay = (percent, isMultiTenant) => {
    const numericPercent = parseFloat(percent);
    const shouldShow = isMultiTenant || numericPercent > 0;
    if (!shouldShow) return '';

    const formattedValue = isMultiTenant
        ? numericPercent.toFixed(2)
        : numericPercent.toFixed(1);
    return ` (${formattedValue}%)`;
};

const Registration = ({ registrationData, endDate, isMultiTenant }) => {
    if (isRegistrationDataEmpty(registrationData)) {
        return <NotEnoughData />;
    }
    const [isToolTipActive, setIsToolTipActive] = useState(false);
    const [toolTipPosition, setToolTipPosition] = useState({ x: 0, y: 0 });
    const populationTotal = Math.abs(registrationData?.populationTotal || 0);
    const registeredUsers = Math.abs(registrationData?.registeredUsers || 0);
    const activeUsers = Math.abs(registrationData?.activeUsers || 0);
    const newUsers = Math.abs(registrationData?.newUsers || 0);

    const formattedAsOfDate = format(
        new Date(endDate),
        DASHBOARD_AS_OF_DATE_FORMAT,
    );

    // Calculate inactive users, and percentages with useMemo for optimal performance
    const inactiveUsers = useMemo(() => {
        return registeredUsers - activeUsers;
    }, [registeredUsers, activeUsers]);

    const registeredPercent = useMemo(
        () => getPercentage(registeredUsers, populationTotal),
        [registeredUsers, populationTotal],
    );

    const activePercent = useMemo(
        () => getPercentage(activeUsers, populationTotal),
        [activeUsers, populationTotal],
    );

    const newUsersPercent = useMemo(
        () => getPercentage(newUsers, populationTotal),
        [newUsers, populationTotal],
    );

    return (
        <>
            <div className="registration-dashboard">
                <div
                    className="registration-chart-section"
                    onMouseEnter={() => {
                        setIsToolTipActive(true);
                    }}
                    onMouseMove={e => {
                        setToolTipPosition({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => {
                        setIsToolTipActive(false);
                    }}
                >
                    <LiquidGauge
                        value={parseFloat(registeredPercent)}
                        width={220}
                        height={220}
                        textSize={1}
                        textOffsetX={0}
                        textOffsetY={0}
                        outerRadius={0.95}
                        innerRadius={0.92}
                        waveAmplitude={2}
                        style={{
                            position: 'absolute',
                            top: '25px',
                            backgroundColor: '#F5F5F5',
                            borderRadius: '90%',
                        }}
                        riseAnimation
                        waveAnimation={false}
                        gradient
                        animate={true}
                        circleStyle={{
                            stroke: 'url(#registrationGradient)',
                            strokeWidth: 8,
                            fill: '#C4C4C4',
                        }}
                        waveColor="#14BA6D"
                        gradientStops={[
                            {
                                key: '0%',
                                stopColor: '#1366FF',
                                stopOpacity: 0.4,
                                offset: '0%',
                            },
                            {
                                key: '100%',
                                stopColor: '#00B69B',
                                stopOpacity: 0.4,
                                offset: '50%',
                            },
                        ]}
                        textRenderer={props => {
                            const value = isMultiTenant
                                ? parseFloat(props.value).toFixed(2)
                                : parseFloat(props.value).toFixed(1);
                            return (
                                <tspan>
                                    <tspan
                                        style={{
                                            fill: '#2F2B43',
                                            fontSize: '36px',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {value}%
                                    </tspan>
                                    <tspan
                                        x="0"
                                        y="1.5em"
                                        style={{
                                            fill: '#2F2B43',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            zIndex: 500,
                                        }}
                                    >
                                        {getText('msg_a844fcf834e7')}
                                    </tspan>
                                </tspan>
                            );
                        }}
                    />
                    <svg>
                        <defs>
                            {getRegistrationPercentageGradient(
                                registeredPercent,
                            )}
                        </defs>
                    </svg>
                </div>
                <div className="registration-metrics-section">
                    <div className="registration-metric">
                        <span className="registration-metric-label">
                            {getText('msg_08cc37160bfa')} {formattedAsOfDate})
                        </span>
                        <span className="registration-metric-value">
                            {populationTotal.toLocaleString()}
                        </span>
                    </div>
                    <div className="registration-metric">
                        <span className="registration-metric-label">
                            {getText('msg_01d34d8f87fd')} {formattedAsOfDate})
                        </span>
                        <span className="registration-metric-value">
                            {registeredUsers.toLocaleString()}
                            {formatPercentageDisplay(
                                registeredPercent,
                                isMultiTenant,
                            )}
                        </span>
                    </div>
                    <div className="registration-metric">
                        <span className="registration-metric-label">
                            {getText('msg_5cf89afb8bc8')}
                        </span>
                        <span className="registration-metric-value">
                            {activeUsers.toLocaleString()}
                        </span>
                    </div>
                    <div className="registration-metric">
                        <span className="registration-metric-label">
                            {getText('msg_3a6d445511d3')}
                        </span>
                        <span className="registration-metric-value">
                            {newUsers.toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>
            <CustomDashboardTooltip
                active={isToolTipActive}
                customContent={renderRegistrationTooltipContent(
                    activeUsers,
                    inactiveUsers,
                    toolTipPosition,
                )}
            />
        </>
    );
};

export default Registration;
